import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-md border transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive: "border-transparent bg-red-background text-red-foreground",
        custom: "border-transparent bg-background text-primary-foreground",
        brand: "border-transparent bg-brand text-black",
        outline: "text-foreground",
      },
      size: {
        xs: "px-0.5 py-0.5 leading-none! text-[12px] font-medium min-w-4 justify-center",
        sm: "px-1 py-0.5 text-xs/none font-medium",
        base: "px-2 py-0.5 text-xs font-medium",
        lg: "px-3 py-1 text-sm font-medium",
      },
      clickable: {
        true: "cursor-pointer",
        false: "",
      },
    },
    compoundVariants: [
      {
        clickable: true,
        variant: "default",
        className: "hover:bg-primary",
      },
      {
        clickable: true,
        variant: "secondary",
        className: "hover:bg-secondary/80 hover:shadow-inset",
      },
      {
        clickable: true,
        variant: "destructive",
        className: "hover:bg-destructive/80",
      },
      {
        clickable: true,
        variant: "brand",
        className: "hover:bg-brand-hover",
      },
      {
        clickable: true,
        variant: "outline",
        className: "hover:bg-accent hover:text-accent-foreground",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "base",
      clickable: false,
    },
  }
)

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  clickable?: boolean
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(({ className, variant, size, clickable, ...props }, ref) => {
  return <div ref={ref} className={cn(badgeVariants({ variant, size, clickable }), className)} {...props} />
})

Badge.displayName = "Badge"

export { Badge, badgeVariants }
